/**
 * Bonsai Mail - Activity Log Page
 * View filing history and activity
 */

import { useEffect, useState } from "react";
import { useBonsai } from "../../contexts/BonsaiContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Input } from "../../components/ui/input";
import { 
  FolderOpen,
  Search,
  RefreshCw,
  ExternalLink,
  Mail,
  Calendar
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { fmtUK } from "../../lib/dateUtils";

export default function BonsaiActivity() {
  const { authenticated, bonsaiApi } = useBonsai();
  
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (authenticated) {
      fetchFilings();
    }
  }, [authenticated]);

  const fetchFilings = async () => {
    setLoading(true);
    try {
      const response = await bonsaiApi.get("/filing/history?limit=100");
      setFilings(response.data.filings || []);
      setTotal(response.data.total || 0);
    } catch (e) {
      console.error("Failed to fetch filings:", e);
    } finally {
      setLoading(false);
    }
  };

  const filteredFilings = filings.filter(filing => 
    filing.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    filing.from_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    filing.sharepoint_path?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    filing.matter_reference?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!authenticated) {
    return (
      <div className="p-8 text-center">
        <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Please sign in to view activity</p>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="bonsai-activity-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Filing History
          </h1>
          <p className="text-slate-500">
            {total} emails filed to SharePoint
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://bonsai-caseminder-portal.online"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors"
            data-testid="hub-link-activity"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            CaseMinder Hub
          </a>
          <Button 
            onClick={fetchFilings} 
            variant="outline" 
            size="sm"
            className="gap-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input 
          placeholder="Search by subject, sender, path, or matter reference..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-navy-hover/60 border-navy-border"
          data-testid="search-input"
        />
      </div>

      {/* Filings List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : filteredFilings.length === 0 ? (
        <Card className="border-navy-border">
          <CardContent className="py-12 text-center">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-white mb-2">No filings yet</h3>
            <p className="text-slate-500">
              {searchQuery ? "No filings match your search" : "Filed emails will appear here"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="filings-list">
          {filteredFilings.map((filing, index) => (
            <Card 
              key={filing.id || index}
              className="border-navy-border hover:shadow-md transition-shadow"
              data-testid={`filing-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-status-green-dim flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-5 h-5 text-status-green" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-medium text-white truncate">
                          {filing.subject || "(No Subject)"}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{filing.from_name || filing.from_address}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {filing.matter_reference && (
                          <Badge variant="outline" className="text-xs">
                            {filing.matter_reference}
                          </Badge>
                        )}
                        <span className="text-xs text-slate-400">
                          {filing.filed_at ? formatDistanceToNow(new Date(filing.filed_at), { addSuffix: true }) : ""}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <FolderOpen className="w-3 h-3" />
                        <span className="truncate max-w-[300px]">{(() => { try { return decodeURIComponent(filing.sharepoint_path || ''); } catch(e) { return filing.sharepoint_path; } })()}</span>
                      </div>
                      
                      {filing.sharepoint_url && (
                        <a 
                          href={filing.sharepoint_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open in SharePoint
                        </a>
                      )}
                    </div>
                    
                    <div className="mt-2 flex items-center gap-4 text-xs text-slate-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {filing.received_date ? fmtUK(filing.received_date, "PP") : "N/A"}
                      </div>
                      {filing.filed_by_name && (
                        <span>Filed by {filing.filed_by_name}</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
